/**
 * Internal Pages Index — browse, search, and manage indexed client pages.
 *
 * Workspace-scoped: reads from the global workspace switcher.
 * Restricted to elevated platform roles.
 */

import React, { useState } from 'react';
import { isElevatedRole } from '@/lib/roles';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useTenantStore } from '@/stores/useTenantStore';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { useJobPoller } from '@/hooks/useJobPoller';
import { apiClient } from '@/services/apiClient';
import { toast } from 'sonner';
import {
  Search,
  Globe,
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  ShieldCheck,
  Loader2,
  Database,
  Check,
  Sparkles,
  Network,
  Zap,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
// DropdownMenu no longer used — replaced by Popover-based ActionPanel
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface IndexedPage {
  id: string;
  tenant_id: string;
  title: string;
  url: string;
  slug: string;
  excerpt: string;
  summary: string | null;
  status: string;
  origin: string;
  wp_content_type: string;
  wordpress_post_id: number | null;
  indexed_at: string | null;
  summary_generated_at: string | null;
}

interface WorkspaceStats {
  tenant_id: string;
  total_pages: number;
  with_summary: number;
  without_summary: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Curated for fast, current community-preferred summary models on this page.
const SUMMARY_MODELS = [
  { value: 'google/gemini-3-flash-preview', label: 'Gemini 3 Flash' },
  { value: 'minimax/minimax-m2.7', label: 'MiniMax M2.7' },
  { value: 'moonshotai/kimi-k2.5', label: 'Kimi K2.5' },
  { value: 'z-ai/glm-5.1', label: 'GLM 5.1' },
];

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

async function fetchPages(params: Record<string, string | number | boolean | undefined>) {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  );
  const { data } = await apiClient.get<{
    items: IndexedPage[];
    total: number;
    page: number;
    page_size: number;
  }>('/internal-pages/', { params: cleanParams });
  return data;
}

async function fetchStats() {
  const { data } = await apiClient.get<{
    workspaces: WorkspaceStats[];
    total_pages: number;
    total_with_summary: number;
  }>('/internal-pages/stats');
  return data;
}



// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

function OriginBadge({ origin }: { origin: string }) {
  const colors: Record<string, string> = {
    plugin: 'bg-teal-light text-teal-dark',
    sitemap: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400',
    generated: 'bg-coral-light text-coral-dark',
    preexisting: 'bg-muted text-muted-foreground',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${colors[origin] || 'bg-muted text-muted-foreground'}`}>
      {origin || '—'}
    </span>
  );
}

function SingleSummaryButton({ pageId, hasSummary, onDone }: { pageId: string; hasSummary: boolean; onDone: () => void }) {
  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(`/internal-pages/generate-summary/${pageId}`);
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Summary ${hasSummary ? 'regenerated' : 'generated'}`);
      onDone();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Summary generation failed');
    },
  });

  return (
    <Button
      variant="ghost"
      size="sm"
      className="text-xs gap-1 h-7"
      onClick={(e) => { e.stopPropagation(); mutation.mutate(); }}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Sparkles className="h-3 w-3" />
      )}
      {hasSummary ? 'Regenerate' : 'Generate'} Summary
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Actions Dropdown + Inline Progress
// ---------------------------------------------------------------------------

function useActiveJobs(tenantId: string) {
  /**Polls /internal-pages/jobs, syncs to notification store, returns tenant jobs.*/
  const { notifications, addNotification, updateNotification } = useNotificationStore();

  const query = useQuery({
    queryKey: ['internal-pages', 'jobs', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<Array<JobWithLogs & {
        tenant_id: string | null;
      }>>('/internal-pages/jobs');
      return data.filter((j) => j.tenant_id === tenantId);
    },
    refetchInterval: (query) => {
      const jobs = query.state.data;
      if (jobs?.some((j) => j.status === 'running')) return 3000;
      return false;
    },
    enabled: !!tenantId,
  });

  // Sync backend jobs → notification store
  React.useEffect(() => {
    if (!query.data) return;
    const backendJobIds = new Set(query.data.map((j) => j.job_id));
    const knownIds = new Set(notifications.map((n) => n.id));

    // Add/update backend jobs in notification store
    for (const job of query.data) {
      if (!knownIds.has(job.job_id)) {
        addNotification({
          id: job.job_id,
          type: job.job_type === 'reindex' ? 'reindex' : 'summary_generation',
          title: job.title,
          message: job.tenant_id || '',
          status: job.status as 'running' | 'done' | 'failed',
          progress: { completed: job.completed, total: job.total, failed: job.failed },
          createdAt: new Date().toISOString(),
        });
      } else {
        updateNotification(job.job_id, {
          status: job.status as 'running' | 'done' | 'failed',
          progress: { completed: job.completed, total: job.total, failed: job.failed },
        });
      }
    }

    // Detect stale "running" notifications — backend has no record (server restarted)
    for (const n of notifications) {
      if (n.status === 'running' && !backendJobIds.has(n.id)) {
        updateNotification(n.id, {
          status: 'failed',
          message: n.message ? `${n.message} (lost — server restarted)` : 'Lost — server restarted',
        } as any);
      }
    }
  }, [query.data]); // eslint-disable-line react-hooks/exhaustive-deps

  return query;
}

interface JobWithLogs {
  job_id: string; job_type: string; title: string; status: string;
  total: number; completed: number; failed: number; error: string | null;
  current_step?: string | null;
  logs?: Array<{ ts: string; msg: string }>;
}

function JobProgressCard({ jobs, onComplete }: {
  jobs: JobWithLogs[];
  onComplete: () => void;
}) {
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set());
  const [expandedLog, setExpandedLog] = React.useState<string | null>(null);
  const prevRunning = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    const currentRunning = new Set(jobs.filter((j) => j.status === 'running').map((j) => j.job_id));
    const justFinished = [...prevRunning.current].filter((id) => !currentRunning.has(id));
    if (justFinished.length > 0) {
      onComplete();
    }
    prevRunning.current = currentRunning;
  }, [jobs, onComplete]);

  const visibleJobs = jobs.filter((j) => !dismissed.has(j.job_id) && (j.status === 'running' || j.status === 'done' || j.status === 'failed'));
  if (visibleJobs.length === 0) return null;

  return (
    <div className="space-y-2">
      {visibleJobs.map((job) => {
        const logs = job.logs || [];
        const isExpanded = expandedLog === job.job_id;

        return (
          <div key={job.job_id} className={`rounded-lg border overflow-hidden ${
            job.status === 'running' ? 'bg-teal-light/30 border-teal/30' :
            job.status === 'done' ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800' :
            'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800'
          }`}>
            <div className="px-4 py-3 flex items-center gap-3">
              {job.status === 'running' && <Loader2 className="h-4 w-4 text-teal animate-spin shrink-0" />}
              {job.status === 'done' && <Check className="h-4 w-4 text-emerald-500 dark:text-emerald-400 shrink-0" />}
              {job.status === 'failed' && <span className="h-4 w-4 text-destructive shrink-0 font-bold">!</span>}

              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{job.title}</div>

                {/* Current step label */}
                {job.status === 'running' && job.current_step && (
                  <div className="text-xs text-teal-dark mt-0.5">{job.current_step}</div>
                )}
                {job.status === 'running' && !job.current_step && (
                  <div className="text-xs text-muted-foreground mt-0.5">Starting...</div>
                )}

                {/* Progress bar — pages count */}
                {job.status === 'running' && job.completed > 0 && (
                  <div className="mt-1.5">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>{job.completed} pages indexed</span>
                    </div>
                    <div className="h-1.5 bg-card rounded-full overflow-hidden">
                      <div className="h-full bg-teal rounded-full transition-all animate-pulse" style={{ width: '100%' }} />
                    </div>
                  </div>
                )}

                {/* Running with 0 pages */}
                {job.status === 'running' && job.completed === 0 && !job.current_step && (
                  <div className="mt-1.5">
                    <div className="h-1.5 bg-card rounded-full overflow-hidden">
                      <div className="h-full bg-teal/50 rounded-full animate-pulse" style={{ width: '30%' }} />
                    </div>
                  </div>
                )}

                {/* Done */}
                {job.status === 'done' && (
                  <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                    Completed — {job.completed} pages{job.failed > 0 ? ` (${job.failed} failed)` : ''}
                  </div>
                )}

                {/* Failed */}
                {job.status === 'failed' && (
                  <div className="text-xs text-destructive mt-0.5">{job.error || 'Unknown error'}</div>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {/* Log toggle */}
                {logs.length > 0 && (
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setExpandedLog(isExpanded ? null : job.job_id)}
                  >
                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                )}
                {job.status !== 'running' && (
                  <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setDismissed((s) => new Set(s).add(job.job_id))}>
                    Dismiss
                  </button>
                )}
              </div>
            </div>

            {/* Activity log */}
            {isExpanded && logs.length > 0 && (
              <div className="border-t px-4 py-2 bg-card/50 space-y-1">
                {logs.map((log, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="text-muted-foreground shrink-0 font-mono w-16">
                      {new Date(log.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                    <span className="text-foreground">{log.msg}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ActionsDropdown({
  selectedTenant,
  onJobStarted,
}: {
  selectedTenant: string;
  onJobStarted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [stepsIndex, setStepsIndex] = useState(true);
  const [stepsCrawl, setStepsCrawl] = useState(false);
  const [stepsSummaries, setStepsSummaries] = useState(true);
  const [mode, setMode] = useState<'missing' | 'all'>('missing');
  const [model, setModel] = useState('google/gemini-3-flash-preview');
  const { addNotification } = useNotificationStore();

  const pipelineMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/internal-pages/reindex', {
        tenant_id: selectedTenant,
        steps_index: stepsIndex,
        steps_crawl: stepsCrawl,
        steps_summaries: stepsSummaries,
        mode,
        summary_model: stepsSummaries ? model : undefined,
      });
      return data;
    },
    onSuccess: (data) => {
      const steps = data.steps || 'index';
      addNotification({
        id: data.job_id,
        type: 'reindex',
        title: `Pipeline (${steps})`,
        message: selectedTenant,
        status: 'running',
        createdAt: new Date().toISOString(),
      });
      onJobStarted();
      setOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err.message);
    },
  });

  const anyStepSelected = stepsIndex || stepsCrawl || stepsSummaries;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          className="bg-coral hover:bg-coral-hover text-white gap-1.5"
          disabled={!selectedTenant || pipelineMutation.isPending}
        >
          {pipelineMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          Actions
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold">Pipeline for {selectedTenant?.split('.')[0]}</h4>
            <p className="text-xs text-muted-foreground mt-0.5">Select steps to run and mode</p>
          </div>

          {/* Steps toggles */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Steps</Label>
            <label className="flex items-center gap-2.5 cursor-pointer group">
              <input type="checkbox" checked={stepsIndex} onChange={(e) => setStepsIndex(e.target.checked)}
                className="rounded border-border text-coral focus:ring-coral h-4 w-4" />
              <div className="flex items-center gap-2 flex-1">
                <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
                <div>
                  <div className="text-sm">Index</div>
                  <div className="text-xs text-muted-foreground">Sitemap / WP plugin scan</div>
                </div>
              </div>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer group">
              <input type="checkbox" checked={stepsCrawl} onChange={(e) => setStepsCrawl(e.target.checked)}
                className="rounded border-border text-coral focus:ring-coral h-4 w-4" />
              <div className="flex items-center gap-2 flex-1">
                <Network className="h-3.5 w-3.5 text-muted-foreground" />
                <div>
                  <div className="text-sm">Crawl</div>
                  <div className="text-xs text-muted-foreground">Follow links from homepage</div>
                </div>
              </div>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer group">
              <input type="checkbox" checked={stepsSummaries} onChange={(e) => setStepsSummaries(e.target.checked)}
                className="rounded border-border text-coral focus:ring-coral h-4 w-4" />
              <div className="flex items-center gap-2 flex-1">
                <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
                <div>
                  <div className="text-sm">Generate Summaries</div>
                  <div className="text-xs text-muted-foreground">AI descriptions for each page</div>
                </div>
              </div>
            </label>
          </div>

          {/* Mode */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Mode</Label>
            <div className="flex gap-2">
              <button
                className={`flex-1 text-xs py-1.5 px-3 rounded-md border transition-colors ${
                  mode === 'missing' ? 'bg-coral/10 border-coral text-coral font-medium' : 'border-border text-muted-foreground hover:border-border'
                }`}
                onClick={() => setMode('missing')}
              >
                New only
              </button>
              <button
                className={`flex-1 text-xs py-1.5 px-3 rounded-md border transition-colors ${
                  mode === 'all' ? 'bg-coral/10 border-coral text-coral font-medium' : 'border-border text-muted-foreground hover:border-border'
                }`}
                onClick={() => setMode('all')}
              >
                Redo all
              </button>
            </div>
          </div>

          {/* Model — only show when summaries enabled */}
          {stepsSummaries && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Model</Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUMMARY_MODELS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              size="sm"
              onClick={() => pipelineMutation.mutate()}
              disabled={pipelineMutation.isPending || !anyStepSelected}
              className="bg-coral hover:bg-coral-hover text-white"
            >
              {pipelineMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Start
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function InternalPagesIndexPage() {
  const { userProfile } = useWorkspaceStore();
  const currentTenant = useTenantStore((s) => s.currentTenant);
  const [search, setSearch] = useState('');
  const [hasSummary, setHasSummary] = useState<string>('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const selectedTenant = currentTenant || '';

  const platformRole = userProfile?.platform_role;
  const isElevated = isElevatedRole(platformRole);

  // Active jobs for this tenant (polls every 3s while running)
  const { data: activeJobs = [], refetch: refetchJobs } = useActiveJobs(selectedTenant);
  const hasRunningJobs = activeJobs.some((j) => j.status === 'running');

  // Stats for all workspaces — refetch every 10s while jobs are running
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['internal-pages', 'stats'],
    queryFn: fetchStats,
    enabled: isElevated,
    staleTime: hasRunningJobs ? 5_000 : 30_000,
    refetchInterval: hasRunningJobs ? 10_000 : false,
  });

  const wsStats = stats?.workspaces?.find((w) => w.tenant_id === selectedTenant);

  // Pages list — scoped to current workspace, refetch every 10s while jobs running
  const { data, isLoading, refetch: refetchPages } = useQuery({
    queryKey: ['internal-pages', 'list', selectedTenant, search, hasSummary, page],
    queryFn: () => fetchPages({
      tenant_id: selectedTenant || undefined,
      search: search || undefined,
      has_summary: hasSummary === 'yes' ? true : hasSummary === 'no' ? false : undefined,
      page,
      page_size: 50,
    }),
    enabled: isElevated && !!selectedTenant,
    refetchInterval: hasRunningJobs ? 10_000 : false,
  });

  const handleRefreshAll = React.useCallback(() => {
    refetchStats();
    refetchPages();
    refetchJobs();
  }, [refetchStats, refetchPages, refetchJobs]);

  if (!isElevated) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <ShieldCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
        <p className="text-muted-foreground">Restricted to Super Admins and Platform Owners.</p>
      </div>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 50);
  const hasIndexedPages = (wsStats?.total_pages ?? 0) > 0;
  const isCheckingPages = !!selectedTenant && (statsLoading || isLoading);

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-teal-light flex items-center justify-center">
            <Database className="h-5 w-5 text-teal-dark" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Internal Pages Index</h1>
            <p className="text-sm text-muted-foreground">
              {!selectedTenant ? (
                'Select a workspace from the top-right switcher'
              ) : isCheckingPages && !wsStats ? (
                <>Checking indexed pages for <span className="font-medium">{selectedTenant}</span>...</>
              ) : (
                <>
                  {wsStats?.total_pages ?? 0} pages indexed for <span className="font-medium">{selectedTenant}</span>
                  {wsStats ? ` (${wsStats.with_summary} with summaries)` : ''}
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Empty state — no workspace selected */}
      {!selectedTenant && (
        <div className="bg-card rounded-lg border py-16 text-center">
          <Globe className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-1">No workspace selected</h3>
          <p className="text-sm text-muted-foreground">
            Use the workspace switcher in the top-right to select a client.
          </p>
        </div>
      )}

      {/* Loading state — checking indexed pages */}
      {isCheckingPages && !hasIndexedPages && (
        <div className="bg-card rounded-lg border py-16 text-center">
          <Loader2 className="h-10 w-10 text-teal animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-1">Checking indexed pages...</h3>
          <p className="text-sm text-muted-foreground">
            Looking up indexed pages for {selectedTenant}
          </p>
        </div>
      )}

      {/* Active jobs progress */}
      {selectedTenant && activeJobs.length > 0 && (
        <JobProgressCard jobs={activeJobs} onComplete={handleRefreshAll} />
      )}

      {/* Empty state — workspace selected but no indexed pages and no running jobs */}
      {selectedTenant && !hasIndexedPages && !isCheckingPages && !activeJobs.some((j) => j.status === 'running') && (
        <div className="bg-card rounded-lg border py-16 text-center">
          <FileText className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-1">No pages indexed yet</h3>
          <p className="text-sm text-muted-foreground mb-6">
            {selectedTenant} hasn't been scanned yet. Get started by indexing pages from their website.
          </p>
          <ActionsDropdown
            selectedTenant={selectedTenant}
            onJobStarted={refetchJobs}
          />
        </div>
      )}

      {/* Filters + Actions toolbar */}
      {selectedTenant && hasIndexedPages && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search title or URL..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9"
            />
          </div>
          <select
            value={hasSummary}
            onChange={(e) => { setHasSummary(e.target.value); setPage(1); }}
            className="border rounded-md px-3 py-2 text-sm bg-card"
          >
            <option value="">All pages</option>
            <option value="yes">With summary</option>
            <option value="no">Missing summary</option>
          </select>
          <div className="ml-auto">
            <ActionsDropdown
              selectedTenant={selectedTenant}
              onJobStarted={refetchJobs}
            />
          </div>
        </div>
      )}

      {/* Table */}
      {selectedTenant && hasIndexedPages && (
        <div className="bg-card rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b bg-muted/50 flex items-center justify-between">
            <span className="text-sm font-semibold">{total} pages</span>
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages || 1}</span>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Title</th>
                <th className="text-center text-xs font-medium text-muted-foreground px-4 py-2.5">Origin</th>
                <th className="text-center text-xs font-medium text-muted-foreground px-4 py-2.5">Summary</th>
                <th className="text-center text-xs font-medium text-muted-foreground px-4 py-2.5 w-16">Link</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={4} className="text-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />No pages found</td></tr>
              ) : (
                items.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr
                      className={`border-b hover:bg-muted/50 cursor-pointer transition-colors ${expandedId === item.id ? 'bg-warm-bg' : ''}`}
                      onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {expandedId === item.id
                            ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                            : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                          <span className="text-sm font-medium truncate max-w-[400px]">{item.title || 'Untitled'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <OriginBadge origin={item.origin} />
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            item.wp_content_type === 'post' ? 'bg-coral-light text-coral-dark' : 'bg-muted text-muted-foreground'
                          }`}>
                            {item.wp_content_type || 'page'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {item.summary ? (
                          <Check className="h-4 w-4 text-emerald-500 dark:text-emerald-400 mx-auto" />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-muted-foreground hover:text-coral"
                          >
                            <ExternalLink className="h-4 w-4 mx-auto" />
                          </a>
                        )}
                      </td>
                    </tr>
                    {expandedId === item.id && (
                      <tr>
                        <td colSpan={4} className="px-4 py-3 bg-warm-bg border-b">
                          <div className="space-y-2 text-sm">
                            <div className="flex items-start gap-2">
                              <span className="text-muted-foreground w-20 shrink-0">URL:</span>
                              <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-coral hover:underline break-all">{item.url}</a>
                            </div>
                            {item.excerpt && (
                              <div className="flex items-start gap-2">
                                <span className="text-muted-foreground w-20 shrink-0">Excerpt:</span>
                                <span className="text-foreground">{item.excerpt}</span>
                              </div>
                            )}
                            <div className="flex items-start gap-2">
                              <span className="text-muted-foreground w-20 shrink-0">Summary:</span>
                              <div className="flex-1">
                                {item.summary ? (
                                  <span className="text-foreground bg-card rounded p-2 border block">{item.summary}</span>
                                ) : (
                                  <span className="text-muted-foreground italic">No summary generated</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between pt-1">
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                {item.indexed_at && <span>Indexed: {new Date(item.indexed_at).toLocaleDateString()}</span>}
                                {item.summary_generated_at && <span>Summary: {new Date(item.summary_generated_at).toLocaleDateString()}</span>}
                                {item.wordpress_post_id && <span>WP ID: {item.wordpress_post_id}</span>}
                                <span>Type: {item.wp_content_type || '—'}</span>
                              </div>
                              <SingleSummaryButton pageId={item.id} hasSummary={!!item.summary} onDone={refetchPages} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
